'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import type { Platform, MobileForensicFamily, MobileForensicViewKind, MobileForensicViewModel } from '@tranquility-ai/theme-components';
import { useMobileForensicViewerData } from '@/hooks/useUfdrViewerData';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { threadPost, threadSummary } from '@/shared/ufdr/toPlatformPost';
import { exportViewModel, processedViewModel } from '@/shared/mobile-forensic/toViewModel';
import { openChatIdFromRoute, splitUfdrChatId } from '@/shared/ufdr-mobile-adapter';
import type { UfdrChatSummary, UfdrExportData } from '@/shared/ufdr/legacy-types';

type LocalSelection = {
  family?: MobileForensicFamily;
  typeId?: string;
  view?: MobileForensicViewKind;
  threadId?: string;
  search: string;
  recordId?: string;
  nodeId?: string;
};

type RelatedTarget = { typeId: string; id: string };

export function relatedFamily(typeId: string): MobileForensicFamily {
  if (typeId === 'contacts') return 'contact';
  if (typeId === 'media') return 'media';
  if (typeId === 'files') return 'files';
  return 'message';
}

export function useMobileForensicView(args: {
  enabled: boolean;
  path?: string | null;
  caseFolderName?: string | null;
  acquisitionId?: number | null;
  artifactType?: string;
  view?: string;
  family?: string;
  search?: string;
  filters?: string;
  record?: string;
  threadId?: string;
  page?: string;
  rowIds?: string[];
  sidecar?: UfdrExportData;
  title?: string;
}) {
  const t = useTimePilotT();
  const mobile = useMobileForensicViewerData({ ...args, section: 'artifacts' });
  const [local, setLocal] = useState<LocalSelection>({
    family: args.family as MobileForensicFamily | undefined,
    typeId: args.artifactType,
    view: args.view as MobileForensicViewKind | undefined,
    threadId: args.threadId,
    search: args.search ?? '',
    recordId: args.record,
  });

  const exportKey = `${args.path ?? ''}\0${args.acquisitionId ?? ''}`;
  const exportKeyRef = useRef(exportKey);
  useEffect(() => {
    // The address bar wins for back, forward, and a thread-only update.
    // A related chat still writes family and threadId in the same update as
    // record, so copying the route here keeps that conversation.
    // The chosen file is not a URL field. A file click writes family=files
    // and must not clear the node it just selected.
    const switchedExport = exportKeyRef.current !== exportKey;
    exportKeyRef.current = exportKey;
    setLocal((current) => ({
      family: args.family as MobileForensicFamily | undefined,
      typeId: args.artifactType,
      view: args.view as MobileForensicViewKind | undefined,
      threadId: args.threadId,
      search: args.search ?? '',
      recordId: args.record,
      nodeId: !switchedExport && args.family === 'files' ? current.nodeId : undefined,
    }));
  }, [
    exportKey,
    args.family,
    args.artifactType,
    args.view,
    args.threadId,
    args.search,
    args.record,
  ]);

  const model = useMemo(() => {
    if (!mobile.matched) {
      return exportViewModel(args.sidecar, local, {
        messages: t('mobileForensic.parsedMessages'),
        contacts: t('mobileForensic.parsedContacts'),
        media: t('mobileForensic.parsedMedia'),
        timeline: t('mobileForensic.parsedTimeline'),
        files: t('mobileForensic.parsedFiles'),
      });
    }
    const chats = mobile.data.chats ?? [];
    const wantedId = mobile.openChatId ?? openChatIdFromRoute(args.threadId, args.artifactType);
    const wantedKey = splitUfdrChatId(wantedId)?.threadKey;
    const cited = wantedId
      ? chats.find((chat) => chat.id === wantedId)
          ?? chats.find((chat) => wantedKey != null && splitUfdrChatId(chat.id)?.threadKey === wantedKey)
      : undefined;
    // A citation names one conversation. Do not substitute the first thread
    // while that conversation is still loading.
    const open = cited ?? (wantedId ? undefined : (chats[0]?.messages ? chats[0] : undefined));
    return processedViewModel({
      title: args.title || mobile.caseName,
      uiConfig: mobile.uiConfig,
      artifactType: mobile.artifactType,
      view: mobile.view as MobileForensicViewKind | undefined,
      rows: mobile.rows as Array<Record<string, unknown>>,
      total: mobile.total,
      page: mobile.page,
      pageSize: mobile.pageSize,
      threads: chats.map(threadSummary),
      activeThread: open?.messages?.length ? threadPost(open) : undefined,
      search: local.search || undefined,
      recordId: local.recordId,
      family: local.family === 'files' ? 'files' : undefined,
      nodeId: local.nodeId,
      tree: local.family === 'files' ? args.sidecar?.tree : undefined,
      status: mobile.loading
        ? { kind: 'loading' }
        : mobile.error
          ? { kind: 'error', message: mobile.error }
          : undefined,
    });
  }, [args.artifactType, args.sidecar, args.threadId, args.title, local, mobile, t]);

  const clearRecord = { recordId: undefined, nodeId: undefined };
  const onOverviewSelect = () => {
    if (mobile.matched) mobile.onArtifactTypeChange('');
    setLocal((current) => ({
      ...current,
      family: undefined,
      typeId: undefined,
      view: undefined,
      threadId: undefined,
      ...clearRecord,
    }));
  };
  const onFamilySelect = (family: MobileForensicFamily) => {
    setLocal((current) => ({ ...current, family, typeId: undefined, view: undefined, threadId: undefined, ...clearRecord }));
  };
  const onTypeSelect = (typeId: string) => {
    if (mobile.matched) mobile.onArtifactTypeChange(typeId);
    setLocal((current) => ({
      ...current,
      family: typeId === 'files' ? 'files' : current.family === 'files' ? undefined : current.family,
      typeId,
      view: undefined,
      threadId: undefined,
      nodeId: undefined,
      recordId: undefined,
    }));
  };
  const onViewChange = (view: MobileForensicViewKind) => {
    if (view === 'record') return;
    if (mobile.matched) {
      mobile.onViewChange(view);
      return;
    }
    setLocal((current) => ({ ...current, view }));
  };
  const onThreadSelect = (threadId: string | undefined) => {
    if (mobile.matched) {
      mobile.onChatSelect(threadId ? { id: threadId } : undefined);
      return;
    }
    setLocal((current) => ({ ...current, threadId }));
  };
  const onSearchChange = (search: string) => {
    setLocal((current) => ({ ...current, search }));
  };
  const onRecordSelect = (recordId: string) => {
    setLocal((current) => ({ ...current, recordId }));
  };
  const onRelatedSelect = (target: RelatedTarget) => {
    const family = relatedFamily(target.typeId);
    if (mobile.matched) {
      mobile.onArtifactTypeChange(target.typeId);
      mobile.onChatSelect({ id: target.id });
    }
    setLocal((current) => ({
      ...current,
      family: mobile.matched ? undefined : family,
      typeId: target.typeId,
      threadId: target.id,
      recordId: target.id,
      nodeId: undefined,
      view: undefined,
    }));
  };
  const onShowInFiles = (nodeId: string) => {
    setLocal((current) => ({
      ...current,
      family: 'files',
      typeId: 'files',
      nodeId,
      view: undefined,
      threadId: undefined,
      recordId: undefined,
    }));
  };

  return {
    ...mobile,
    model,
    onOverviewSelect,
    onFamilySelect,
    onTypeSelect,
    onViewChange,
    onThreadSelect,
    onSearchChange,
    onRecordSelect,
    onRelatedSelect,
    onShowInFiles,
  };
}

export type { Platform, UfdrChatSummary };
